/**
 * @description Main list view for the Recurring Plans tab in Transactions.
 * Shows statistics bar, filter controls, and paginated plan table.
 *
 * Key features:
 * - Statistics: active count, MRR, ARR, upcoming revenue, charges this week
 * - Filters: status dropdown, search
 * - Table: customer, amount, frequency, next charge, status, card, actions
 * - Actions: View (row); pause/resume/cancel are inside the slide-over
 *   (RecurringPlanDetail) where permission gating is handled correctly.
 * - Permission-gated: recurring.view for list, recurring.create for "New Plan" button
 *
 * @dependencies
 * - recurringPlanService: API client
 * - RecurringStatistics: Statistics bar component
 * - RecurringPlanRow: Table row component
 * - CreateRecurringPlanModal: Plan creation form
 * - RecurringPlanDetail: Slide-over detail view (pause/resume/cancel live here)
 */

import { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import recurringPlanService from '../../services/api/recurringPlanService';
import RecurringStatistics from './RecurringStatistics';
import RecurringPlanRow from './RecurringPlanRow';
import CreateRecurringPlanModal from './CreateRecurringPlanModal';
import RecurringPlanDetail from './RecurringPlanDetail';

export default function RecurringPlansList({ showCreateModal: externalShowCreate, onCreateModalClose }) {
  const [plans, setPlans] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // Open create modal when triggered from parent (Transactions Create dropdown)
  useEffect(() => {
    if (externalShowCreate) {
      setShowCreateModal(true);
    }
  }, [externalShowCreate]);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await recurringPlanService.getPlans({
        ...filters,
        page,
        limit: 25
      });
      setPlans(result.plans || []);
      setTotalPages(result.totalPages || 1);
    } catch (err) {
      setError('Failed to load recurring plans');
      console.error('Failed to fetch recurring plans:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  const fetchStatistics = useCallback(async () => {
    try {
      setStatsLoading(true);
      const result = await recurringPlanService.getStatistics();
      setStatistics(result.statistics);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handlePlanCreated = () => {
    setShowCreateModal(false);
    if (onCreateModalClose) onCreateModalClose();
    setPage(1);
    fetchPlans();
    fetchStatistics();
  };

  const handleCreateModalClose = () => {
    setShowCreateModal(false);
    if (onCreateModalClose) onCreateModalClose();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  return (
    <div>
      {/* Statistics */}
      <RecurringStatistics statistics={statistics} loading={statsLoading} />

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Status filter */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </select>

          {/* Search */}
          <div className="relative flex-1 sm:flex-initial">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search plans..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          <button
            onClick={() => { fetchPlans(); fetchStatistics(); }}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-red-500">
          <p>{error}</p>
          <button onClick={fetchPlans} className="mt-2 text-brand underline text-sm">
            Try again
          </button>
        </div>
      ) : plans.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <p className="text-lg font-medium mb-1">No recurring plans</p>
          <p className="text-sm">
            {filters.status || filters.search
              ? 'No plans match your filters.'
              : 'Create your first recurring payment plan to get started.'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Frequency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Next Charge</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Card</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {plans.map((plan) => (
                    <RecurringPlanRow
                      key={plan.id}
                      plan={plan}
                      onView={setSelectedPlanId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 border rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 border rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateRecurringPlanModal
          onClose={handleCreateModalClose}
          onCreated={handlePlanCreated}
        />
      )}

      {/* Detail Slide-over (Headless UI controls visibility via Transition.Root show) */}
      <RecurringPlanDetail
        planId={selectedPlanId}
        onClose={() => setSelectedPlanId(null)}
        onPlanUpdated={() => { fetchPlans(); fetchStatistics(); }}
      />
    </div>
  );
}
